import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Zap, 
  Award, 
  Users, 
  ChevronDown, 
  FileText,
  CheckCircle,
  ArrowRight,
  Star,
  Shield,
  Activity,
  Lightbulb
} from 'lucide-react';
import HeartLogo from './HeartLogo';
import SynergizeProtocol from './SynergizeProtocol';
import Publications from './Publications';
import ClinicalStudy from './ClinicalStudy';
import ResearchSection from './ResearchSection';

const NyquistLanding = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [showSynergize, setShowSynergize] = useState(false);
  const [showPublications, setShowPublications] = useState(false);
  const [showClinicalStudy, setShowClinicalStudy] = useState(false);
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting
          }));
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('section[id]').forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  if (showSynergize) {
    return <SynergizeProtocol onBack={() => setShowSynergize(false)} />;
  }

  if (showPublications) {
    return <Publications onBack={() => setShowPublications(false)} />;
  }

  if (showClinicalStudy) {
    return <ClinicalStudy onBack={() => setShowClinicalStudy(false)} />;
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-gray-900/80 backdrop-blur-xl border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-4"
            >
              <HeartLogo className="w-14 h-14" />
              <span className="text-2xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                Nyquist Therapeutics
              </span>
            </motion.div>
            
            <div className="hidden md:flex space-x-8 text-sm">
              {['Science', 'Technology', 'Team', 'Partners', 'Research', 'Publications', 'Synergize', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => {
                    if (item === 'Synergize') {
                      setShowSynergize(true);
                    } else if (item === 'Publications') {
                      setShowPublications(true);
                    } else {
                      document.getElementById(item.toLowerCase())?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="text-gray-300 hover:text-teal-400 transition-colors duration-200"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(17, 24, 39, 0.8), rgba(17, 24, 39, 0.9)), url('https://images.unsplash.com/photo-1698306642516-9841228dcff3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxjYXJkaW92YXNjdWxhciUyMHRlY2hub2xvZ3l8ZW58MHx8fGJsdWV8MTc1MDgwNzc1MHww&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-900/20 to-gray-900/80 z-10" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Featured Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1, duration: 0.8 }}
              className="flex justify-center mb-6"
            >
              <HeartLogo className="w-32 h-32 md:w-40 md:h-40" animate={true} />
            </motion.div>
            
            <motion.h1 
              className="text-5xl md:text-7xl font-bold leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Advancing
              </span>
              <br />
              <span className="text-white">Cardiovascular</span>
              <br />
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Performance
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-2xl md:text-3xl font-light italic text-teal-400 max-w-4xl mx-auto mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Exploring the limits of Cardiovascular Supplementation and Photoceuticals
            </motion.p>
            
            <motion.p 
              className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              Pioneering the future of cardiac health through novel supplemental compounds 
              and advanced photobiomodulation technology. Optimizing performance for both 
              therapeutic recovery and elite athletic achievement.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <button 
                onClick={() => setShowSynergize(true)}
                className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl hover:shadow-teal-500/25 flex items-center space-x-2"
              >
                <span>Explore Synergize Protocol</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              
              <button 
                onClick={() => setShowClinicalStudy(true)}
                className="border-2 border-gray-400 hover:border-teal-400 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:bg-teal-400/10 flex items-center space-x-2"
              >
                <FileText className="w-5 h-5" />
                <span>First in Man Clinical Study</span>
              </button>
            </motion.div>
          </motion.div>
        </div>
        
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="w-8 h-8 text-teal-400" />
        </motion.div>
      </section>

      {/* Synergize Protocol Highlight */}
      <section className="py-20 bg-gradient-to-r from-teal-900/20 via-gray-800 to-teal-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            {...fadeInUp}
          >
            <div className="inline-flex items-center space-x-2 bg-teal-500/20 text-teal-400 rounded-full px-4 py-2 text-sm font-medium mb-6">
              <Star className="w-4 h-4" />
              <span>Flagship Protocol</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-teal-400 to-teal-600 bg-clip-text text-transparent">
                SYNERGIZE
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Revolutionary synergistic utilisation of targeted supplemental compounds with precision 
              photobiomodulation to optimize ATP generation and enhance the energy required for cardiac diastolic function.
            </p>
            <button 
              onClick={() => setShowSynergize(true)}
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-teal-600 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Discover the Protocol
            </button>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mt-16">
            {[
              {
                title: "ATP Enhancement",
                description: "Synergistic substrate loading and photobiomodulation activation",
                icon: Zap
              },
              {
                title: "Cardiac Optimization",
                description: "Improved diastolic function through enhanced cellular energy",
                icon: Heart
              },
              {
                title: "Clinical Validation",
                description: "First-in-man E/A ratio data demonstrates protocol effectiveness",
                icon: Activity
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-teal-500/30 hover:border-teal-400 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-teal-400 mb-4">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Science Section */}
      <section id="science" className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-primary-400 to-cardio-400 bg-clip-text text-transparent">
                Advanced Science
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our breakthrough approach combines cutting-edge supplemental compounds with 
              precision photobiomodulation to optimize cardiac mitochondrial function.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: "Cardiac Mitochondrial Enhancement",
                description: "Novel compounds specifically designed to optimize cardiac cellular energy production and efficiency.",
                image: "https://images.unsplash.com/photo-1576157023870-5185efcd17ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxjZWxsdWxhciUyMGhlYWx0aHxlbnwwfHx8Ymx1ZXwxNzUwODA3NzgzfDA&ixlib=rb-4.1.0&q=85"
              },
              {
                icon: Zap,
                title: "Precision Photobiomodulation",
                description: "Advanced light therapy protocols targeting specific cellular pathways for enhanced cardiovascular function.",
                image: "https://images.unsplash.com/photo-1557508981-e1bd0979ef76?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxsaWdodCUyMHRoZXJhcHl8ZW58MHx8fGJsdWV8MTc1MDgwNzc2N3ww&ixlib=rb-4.1.0&q=85"
              },
              {
                icon: Activity,
                title: "Dual-Target Optimization",
                description: "Synergistic approach benefiting both therapeutic recovery and peak athletic performance.",
                image: "https://images.unsplash.com/photo-1530138897365-a9615c610099?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxhdGhsZXRpYyUyMHBlcmZvcm1hbmNlfGVufDB8fHxibHVlfDE3NTA4MDc3NTl8MA&ixlib=rb-4.1.0&q=85"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="group relative bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 hover:border-primary-500/50 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div 
                  className="absolute inset-0 rounded-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-300"
                  style={{
                    backgroundImage: `url('${item.image}')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-cardio-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-primary-400 transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Expert Team Section */}
      <section id="team" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-primary-400 to-cardio-400 bg-clip-text text-transparent">
                Expert Leadership
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Leading cardiovascular and photobiomodulation specialists driving innovation 
              at the intersection of medicine and technology.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {[
              {
                name: "Dr. Scott W Murray",
                title: "Consultant Cardiologist",
                credentials: "Venturi Cardiology",
                specialization: "Cardiovascular Prevention & Optimization",
                description: "Leading expert in contemporary cardiovascular prevention strategies with extensive experience in cardiac pathology management and athletic performance optimization.",
                image: "https://static.wixstatic.com/media/e88f5c_5780cd8df3b643a18d63580746486de2~mv2.jpg/v1/fill/w_400,h_400,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/e88f5c_5780cd8df3b643a18d63580746486de2~mv2.jpg"
              },
              {
                name: "James D Carroll",
                title: "CEO, Thor Photomedicine Ltd",
                credentials: "Thor Photomedicine Ltd • World Expert in Photobiomodulation",
                specialization: "Advanced Light Therapy Protocols",
                description: "Internationally recognized authority in photobiomodulation technology with pioneering research in therapeutic light applications for cardiovascular health.",
                image: "https://www.a4m.com/assets/images/a4m-2017/staff/James_Carroll.jpg"
              }
            ].map((expert, index) => (
              <motion.div
                key={index}
                className="group relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8 border border-gray-700 hover:border-primary-500/50 transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="relative z-10">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-primary-500/50 group-hover:border-primary-400 transition-colors duration-300">
                      <img
                        src={expert.image}
                        alt={expert.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white group-hover:text-primary-400 transition-colors duration-300">
                        {expert.name}
                      </h3>
                      <p className="text-primary-400 font-semibold">{expert.title}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-gray-700/50 rounded-lg p-4">
                      <p className="text-cardio-400 font-semibold text-sm uppercase tracking-wide mb-1">
                        Affiliations
                      </p>
                      <p className="text-white">{expert.credentials}</p>
                    </div>
                    
                    <div className="bg-gray-700/50 rounded-lg p-4">
                      <p className="text-performance-400 font-semibold text-sm uppercase tracking-wide mb-1">
                        Specialization
                      </p>
                      <p className="text-white">{expert.specialization}</p>
                    </div>
                    
                    <p className="text-gray-300 leading-relaxed">
                      {expert.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Showcase */}
      <section id="technology" className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-primary-400 to-cardio-400 bg-clip-text text-transparent">
                Advanced Technology
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Precision-engineered solutions combining biochemical innovation with 
              cutting-edge photobiomodulation protocols.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-gray-900/70 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-cardio-500 rounded-lg flex items-center justify-center">
                    <Lightbulb className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Novel Compounds</h3>
                </div>
                <ul className="space-y-3">
                  {[
                    "Targeted cardiac mitochondrial enhancement",
                    "Optimized bioavailability and absorption",
                    "Synergistic formulation design",
                    "Evidence-based therapeutic dosing"
                  ].map((item, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-performance-400 flex-shrink-0" />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-900/70 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-cardio-500 to-performance-500 rounded-lg flex items-center justify-center">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Photobiomodulation</h3>
                </div>
                <ul className="space-y-3">
                  {[
                    "Precision wavelength optimization",
                    "Cellular energy pathway targeting",
                    "Non-invasive therapeutic delivery",
                    "Quantified performance metrics"
                  ].map((item, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-primary-400 flex-shrink-0" />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div 
                className="rounded-3xl h-96 bg-cover bg-center relative overflow-hidden"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1617526380947-7a129e1ef767?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxwaG90b2Jpb21vZHVsYXRpb258ZW58MHx8fGJsdWV8MTc1MDgwNzc3NHww&ixlib=rb-4.1.0&q=85')`
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent" />
                <div className="absolute bottom-8 left-8 right-8">
                  <h4 className="text-2xl font-bold text-white mb-2">
                    Cellular Innovation
                  </h4>
                  <p className="text-gray-300">
                    Advanced imaging of enhanced mitochondrial function through our proprietary protocols.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technology Partners Section */}
      <section id="partners" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-teal-400 to-primary-400 bg-clip-text text-transparent">
                Technology Partners
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Collaborating with world-leading technology partners to deliver cutting-edge 
              cardiovascular optimization solutions.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            {[
              {
                name: "Thor Photomedicine Ltd",
                website: "https://thorlaser.com",
                description: "World-leading photobiomodulation technology and laser therapy systems",
                specialization: "Advanced Light Therapy Systems",
                expert: "James D Carroll - World Expert in Photobiomodulation",
                icon: Lightbulb
              },
              {
                name: "Venturi Cardiology",
                website: "https://www.venturicardiology.com",
                description: "Cardiovascular health optimization and prevention protocols",
                specialization: "Cardiac Prevention & Optimization",
                expert: "Dr. Scott W Murray - Consultant Cardiologist",
                icon: Heart
              }
            ].map((partner, index) => (
              <motion.div
                key={index}
                className="group bg-gray-800/50 backdrop-blur-sm rounded-3xl p-8 border border-gray-700 hover:border-teal-500/50 transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="text-center space-y-6">
                  <div className="w-20 h-20 bg-gradient-to-r from-teal-500 to-teal-600 rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                    <partner.icon className="w-10 h-10 text-white" />
                  </div>
                  
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-teal-400 transition-colors duration-300">
                      {partner.name}
                    </h3>
                    <p className="text-teal-400 font-semibold text-sm uppercase tracking-wide mb-4">
                      {partner.specialization}
                    </p>
                  </div>
                  
                  <p className="text-gray-300 leading-relaxed mb-6">
                    {partner.description}
                  </p>
                  
                  <div className="bg-gray-700/50 rounded-lg p-4 mb-6">
                    <p className="text-gray-400 text-sm">
                      {partner.expert}
                    </p>
                  </div>
                  
                  <a
                    href={partner.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-teal-600 px-6 py-3 rounded-lg font-semibold text-white transition-all duration-300 transform hover:scale-105"
                  >
                    <span>Visit {partner.name}</span>
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Section */}
      <ResearchSection />

      {/* Clinical Services Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-primary-400 to-cardio-400 bg-clip-text text-transparent">
                Clinical Services
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Professional cardiovascular care through our clinical practice, 
              integrating cutting-edge research with patient-centered medicine.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <motion.div
              className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-12 border border-gray-700 hover:border-primary-500/50 transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="text-center space-y-8">
                <div className="w-24 h-24 bg-gradient-to-r from-primary-500 to-cardio-500 rounded-full flex items-center justify-center mx-auto">
                  <Shield className="w-12 h-12 text-white" />
                </div>
                
                <div>
                  <h3 className="text-3xl font-bold text-white mb-4">
                    Venturi Cardiology
                  </h3>
                  <p className="text-primary-400 font-semibold text-lg mb-6">
                    Professional Cardiovascular Care
                  </p>
                </div>
                
                <p className="text-xl text-gray-300 leading-relaxed mb-8">
                  Experience comprehensive cardiovascular care through our specialized 
                  clinical practice. Dr. Scott W Murray and our expert team provide 
                  advanced cardiac assessment, prevention strategies, and personalized 
                  treatment protocols.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-700/50 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-white mb-3">Clinical Services</h4>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li>• Comprehensive cardiac assessment</li>
                      <li>• Preventive cardiology protocols</li>
                      <li>• Advanced diagnostic imaging</li>
                      <li>• Personalized treatment plans</li>
                    </ul>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-white mb-3">Research Integration</h4>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li>• Clinical trial participation</li>
                      <li>• Cutting-edge therapy access</li>
                      <li>• Evidence-based protocols</li>
                      <li>• Advanced monitoring systems</li>
                    </ul>
                  </div>
                </div>
                
                <a
                  href="https://www.venturicardiology.com"
                  target="_blank"  
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-3 bg-gradient-to-r from-primary-600 to-cardio-600 hover:from-primary-500 hover:to-cardio-500 px-8 py-4 rounded-lg font-bold text-lg text-white transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  <Shield className="w-5 h-5" />
                  <span>Visit Venturi Cardiology</span>
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            className="space-y-8"
            {...fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-teal-400 to-primary-400 bg-clip-text text-transparent">
                Contact Us
              </span>
            </h2>
            
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Ready to explore the future of cardiovascular optimization? 
              Get in touch with our expert team.
            </p>
            
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
              <div className="flex items-center justify-center space-x-4 mb-6">
                <HeartLogo className="w-12 h-12" animate={true} />
                <div>
                  <h3 className="text-2xl font-bold text-white">Nyquist Therapeutics</h3>
                  <p className="text-gray-400">Cardiovascular Optimization Specialists</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <a
                  href="mailto:team@nyquisttherapeutics.com"
                  className="inline-flex items-center space-x-3 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-teal-600 px-8 py-4 rounded-lg font-bold text-lg text-white transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  <span>team@nyquisttherapeutics.com</span>
                </a>
                
                <div className="grid md:grid-cols-2 gap-4 mt-8">
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-white mb-2">Professional Inquiries</h4>
                    <p className="text-gray-300 text-sm">Clinical partnerships and research collaboration</p>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-white mb-2">Patent & IP Matters</h4>
                    <p className="text-gray-300 text-sm">Intellectual property and licensing inquiries</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-900 via-cardio-900 to-teal-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            className="space-y-8"
            {...fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Ready to Optimize Your
              <br />
              <span className="bg-gradient-to-r from-teal-400 to-primary-400 bg-clip-text text-transparent">
                Cardiovascular Performance?
              </span>
            </h2>
            
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Join the next generation of cardiovascular optimization. Contact our expert team 
              to learn more about our breakthrough protocols.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button className="bg-white text-teal-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl">
                Schedule Consultation
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-teal-900 transition-all duration-300">
                Download Research
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <HeartLogo className="w-10 h-10" />
                <span className="text-xl font-bold bg-gradient-to-r from-teal-400 to-teal-600 bg-clip-text text-transparent">
                  Nyquist Therapeutics
                </span>
              </div>
              <p className="text-gray-400 max-w-md">
                Pioneering the future of cardiovascular health through innovative science and 
                advanced photobiomodulation technology.
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Research</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Clinical Studies</li>
                <li>Publications</li>
                <li>Technology</li>
                <li>Protocols</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="mailto:team@nyquisttherapeutics.com" className="hover:text-teal-400 transition-colors">Professional Inquiry</a></li>
                <li><a href="mailto:team@nyquisttherapeutics.com" className="hover:text-teal-400 transition-colors">Research Collaboration</a></li>
                <li><a href="mailto:team@nyquisttherapeutics.com" className="hover:text-teal-400 transition-colors">Clinical Partnerships</a></li>
                <li><a href="mailto:team@nyquisttherapeutics.com" className="hover:text-teal-400 transition-colors">Media Relations</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Nyquist Therapeutics. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NyquistLanding;